import { EmailContentSource } from '@anduin/api';
import {
  buildStepToWorkflowMap,
  EventType,
  FailureContext,
  generateEventTypeMapDefaults,
  StepToWorkflowMap,
  WorkflowEventEventTypeMap,
  WorkflowEventMetaDataMapWithDefaults,
} from '@anduin/shared';

/**
 * Workflow taxonomy owned by the email-delivery-hub module. Each value is the `workflow`
 * string emitted on tracking events for funnel grouping in downstream analytics.
 */
export enum EmailDeliveryWorkflowType {
  DELIVERY = 'EMAIL_DELIVERY',
}

/**
 * Steps emitted by the Email Delivery Hub. Row-action async steps follow the
 * CLICKED → (DISMISSED | (SUBMITTED → SUCCEEDED | FAILED)) lifecycle so analytics
 * can distinguish abandoned confirms from completed attempts.
 */
export enum EmailDeliveryStep {
  VIEWED = 'EMAIL_DELIVERY_VIEWED',
  TAB_SWITCHED = 'EMAIL_DELIVERY_TAB_SWITCHED',
  SEARCHED = 'EMAIL_DELIVERY_SEARCHED',
  COLUMN_FILTER_CHANGED = 'EMAIL_DELIVERY_COLUMN_FILTER_CHANGED',
  SORT_CHANGED = 'EMAIL_DELIVERY_SORT_CHANGED',
  ROW_ACTION_CLICKED = 'EMAIL_DELIVERY_ROW_ACTION_CLICKED',
  ROW_ACTION_DISMISSED = 'EMAIL_DELIVERY_ROW_ACTION_DISMISSED',
  ROW_ACTION_SUBMITTED = 'EMAIL_DELIVERY_ROW_ACTION_SUBMITTED',
  ROW_ACTION_SUCCEEDED = 'EMAIL_DELIVERY_ROW_ACTION_SUCCEEDED',
  ROW_ACTION_FAILED = 'EMAIL_DELIVERY_ROW_ACTION_FAILED',
  REBUILD_NOTICE_SHOWN = 'EMAIL_DELIVERY_REBUILD_NOTICE_SHOWN',
  REBUILD_NOTICE_OPTED_OUT = 'EMAIL_DELIVERY_REBUILD_NOTICE_OPTED_OUT',
}

/** Union of every step enum value owned by the email-delivery-hub module. */
export type EmailDeliveryWorkflowStep = EmailDeliveryStep;

/** Tab the user is currently viewing on the Email Delivery Hub. */
export type EmailDeliveryTabLabel = 'SENT' | 'PENDING' | 'FAILED';

/** Vocabulary for row-level actions across the three tabs and the shared resend dialog. */
export type EmailDeliveryRowAction = 'RETRY' | 'DISMISS' | 'UNDISMISS' | 'RESEND';

/** Sort direction stamped on `EMAIL_DELIVERY_SORT_CHANGED`. */
export type EmailDeliverySortDirection = 'asc' | 'desc' | null;

/** Context fields stamped onto every Email Delivery event by `EmailDeliveryTrackService`. */
export interface EmailDeliveryBaseMetadata {
  activeTab: EmailDeliveryTabLabel;
  pmsSystem: string;
  failedTabAvailable: boolean;
  /**
   * Comma-bracketed dimension list: leading + trailing comma, alphabetised. Examples:
   *   `` (none),
   *   `,column:status,search,`.
   */
  activeFilters: string;
}

export interface EmailDeliveryTabSwitchedMetadata extends EmailDeliveryBaseMetadata {
  fromTab: EmailDeliveryTabLabel;
  toTab: EmailDeliveryTabLabel;
}

export interface EmailDeliverySearchedMetadata extends EmailDeliveryBaseMetadata {
  queryLength: number;
}

export interface EmailDeliveryColumnFilterChangedMetadata extends EmailDeliveryBaseMetadata {
  columnId: string;
}

export interface EmailDeliverySortChangedMetadata extends EmailDeliveryBaseMetadata {
  columnId: string;
  direction: EmailDeliverySortDirection;
}

/**
 * Disjoint ID spaces — exactly one of `outboundEmailV4Id`, `outboundEmailId`, `unsentEmailId`
 * is populated per row-action event. Sent-tab V4 rows carry `outboundEmailV4Id`; legacy V3
 * rows carry `outboundEmailId`; Pending/Failed rows carry `unsentEmailId`. Mirrors the
 * `ResendEmailRequest` / `RecipientEditAllowedParams` API contracts so V4-vs-legacy
 * migration is visible in dashboards.
 */
export interface EmailDeliveryRowActionMetadata extends EmailDeliveryBaseMetadata {
  action: EmailDeliveryRowAction;
  outboundEmailV4Id?: number;
  outboundEmailId?: number;
  unsentEmailId?: number;
  emailType?: string;
  /**
   * Which content tier the resend would use, when the preview endpoint reported one. Pairs the
   * funnel with the backend's `postmark_resend_template_rebuild` Prometheus counter.
   */
  contentSource?: EmailContentSource;
}

export interface EmailDeliveryRowActionSucceededMetadata extends EmailDeliveryRowActionMetadata {
  operationDurationMs: number;
}

export interface EmailDeliveryRowActionFailedMetadata extends EmailDeliveryRowActionMetadata, FailureContext {
  operationDurationMs: number;
}

interface EmailDeliveryStepMetaDataOverrides {
  [EmailDeliveryStep.VIEWED]: EmailDeliveryBaseMetadata;
  [EmailDeliveryStep.TAB_SWITCHED]: EmailDeliveryTabSwitchedMetadata;
  [EmailDeliveryStep.SEARCHED]: EmailDeliverySearchedMetadata;
  [EmailDeliveryStep.COLUMN_FILTER_CHANGED]: EmailDeliveryColumnFilterChangedMetadata;
  [EmailDeliveryStep.SORT_CHANGED]: EmailDeliverySortChangedMetadata;
  [EmailDeliveryStep.ROW_ACTION_CLICKED]: EmailDeliveryRowActionMetadata;
  [EmailDeliveryStep.ROW_ACTION_DISMISSED]: EmailDeliveryRowActionMetadata;
  [EmailDeliveryStep.ROW_ACTION_SUBMITTED]: EmailDeliveryRowActionMetadata;
  [EmailDeliveryStep.ROW_ACTION_SUCCEEDED]: EmailDeliveryRowActionSucceededMetadata;
  [EmailDeliveryStep.ROW_ACTION_FAILED]: EmailDeliveryRowActionFailedMetadata;
  [EmailDeliveryStep.REBUILD_NOTICE_SHOWN]: EmailDeliveryRowActionMetadata;
  [EmailDeliveryStep.REBUILD_NOTICE_OPTED_OUT]: EmailDeliveryRowActionMetadata;
}

export type EmailDeliveryWorkflowStepMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  EmailDeliveryWorkflowStep,
  EmailDeliveryStepMetaDataOverrides
>;

/**
 * Step-specific metadata with the base context fields removed. `EmailDeliveryTrackService.trackWorkflow`
 * accepts this shape so call sites pass only the per-step extras; the wrapper resolves base fields
 * at emit time.
 */
export type EmailDeliveryStepMetadataInput<E extends EmailDeliveryWorkflowStep> = Omit<
  EmailDeliveryWorkflowStepMetaDataMap[E],
  keyof EmailDeliveryBaseMetadata
>;

const EMAIL_DELIVERY_WORKFLOW_STEPS = {
  [EmailDeliveryWorkflowType.DELIVERY]: EmailDeliveryStep,
} as const;

export const EMAIL_DELIVERY_STEP_TO_WORKFLOW: StepToWorkflowMap<typeof EMAIL_DELIVERY_WORKFLOW_STEPS> =
  buildStepToWorkflowMap(EMAIL_DELIVERY_WORKFLOW_STEPS);

export const EMAIL_DELIVERY_STEP_TO_EVENT_TYPE: WorkflowEventEventTypeMap<EmailDeliveryWorkflowStep> = {
  ...generateEventTypeMapDefaults(EmailDeliveryStep, EventType.USER),
  [EmailDeliveryStep.ROW_ACTION_SUCCEEDED]: EventType.SYSTEM,
  [EmailDeliveryStep.ROW_ACTION_FAILED]: EventType.ERROR,
  [EmailDeliveryStep.REBUILD_NOTICE_SHOWN]: EventType.SYSTEM,
};
